import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as LoadingIcons from "react-loading-icons";
import { FaUser, FaFilePdf, FaCalendarAlt, FaInfoCircle, FaStickyNote } from "react-icons/fa";
import { useSelector } from "react-redux";
import { getAvoir } from "../services/AvoirsService";

const ConsulterAvoir = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const token = useSelector((state) => state.auth.token);

    const [avoir, setAvoir] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedPdf, setSelectedPdf] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);

    const formatMoney = (value) => (value || 0).toFixed(2) + " €";
    const formatRate = (rate) => (parseFloat(rate) % 1 === 0 ? parseInt(rate) : parseFloat(rate).toFixed(1));

    useEffect(() => {
        const fetchData = async () => {
            try {
                const resAvoir = await getAvoir(id);
                setAvoir(resAvoir);
            } catch (err) {
                toast.error(err.message);
                navigate("/EspaceClient/avoirs");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, navigate]);

    // 🔹 TVA par taux
    const tvaParTaux = useMemo(() => {
        const result = {};
        avoir?.lignes?.forEach(ligne => {
            const totalHT = parseFloat(ligne.total_ligne_centimes) || 0;
            const tauxTVA = parseFloat(ligne.taux_tva) || 0;
            const montantTVA = totalHT * tauxTVA / 100;
            const key = tauxTVA.toFixed(2);
            if (!result[key]) result[key] = 0;
            result[key] += montantTVA;
        });
        return result;
    }, [avoir]);

    // 🔹 Totaux HT / TVA / TTC / Net à payer
    const totaux = useMemo(() => {
        if (!avoir) return { sous_total_centimes: 0, tva_centimes: 0, total_centimes: 0, net_a_payer: 0 };

        let sous_total = 0;
        let tva = 0;

        avoir.lignes.forEach(ligne => {
            const totalHT = parseFloat(ligne.total_ligne_centimes) || 0;
            const tauxTVA = parseFloat(ligne.taux_tva) || 0;
            sous_total += totalHT;
            tva += totalHT * tauxTVA / 100;
        });

        const totalTTC = sous_total + tva;
        const totalFactureTTC = parseFloat(avoir.facture?.total_centimes) || 0;
        const netAPayer = totalFactureTTC - totalTTC;

        return {
            sous_total_centimes: sous_total,
            tva_centimes: tva,
            total_centimes: totalTTC,
            net_a_payer: netAPayer
        };
    }, [avoir]);



    const openPdf = (id) => {
        const pdfUrl = `http://localhost:8000/api/v1/avoirs/${id}/pdf?token=${token}`;
        setSelectedPdf(pdfUrl);
        setPdfLoading(true);
    };

    if (loading) return (
        <div className="flex flex-col justify-center min-h-screen items-center">
            <LoadingIcons.SpinningCircles stroke="#2218eeff" fill="#2218eeff" strokeWidth={3} height={40} width={40} />
            <span className="ml-3 text-gray-600 font-medium">Chargement de l'avoir...</span>
        </div>
    );

    if (!avoir) return null;

    const emetteur = avoir.emetteur;
    const client = avoir.facture?.client || avoir.client;

    return (
        <div className="min-h-screen p-6 flex flex-col gap-6">
            <ToastContainer position="top-right" autoClose={3000} />

            {/* En-tête */}
            <div className="flex justify-between items-center">
                <h1 className="font-bold text-2xl">Avoir #{avoir.numero}</h1>
                <button
                    onClick={() => openPdf(avoir.id)}
                    className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                >
                    <FaFilePdf /> Télécharger PDF
                </button>
            </div>

            {/* Émetteur & Destinataire */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Émetteur */}
                <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-2">
                    <h2 className="font-semibold mb-2">
                        <FaUser className="inline-block mr-2" /> Émetteur
                    </h2>
                    {emetteur?.nom_complet && <p><FaUser className="inline-block mr-2" /> {emetteur.nom_complet}</p>}
                    {emetteur?.email && <p><FaInfoCircle className="inline-block mr-2" /> {emetteur.email}</p>}
                    {emetteur?.telephone && <p><FaCalendarAlt className="inline-block mr-2" /> {emetteur.telephone}</p>}
                    {emetteur?.siret && <p><FaStickyNote className="inline-block mr-2" /> {emetteur.siret}</p>}
                </div>

                {/* Destinataire */}
                <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-2">
                    <h2 className="font-semibold mb-2">
                        <FaUser className="inline-block mr-2" /> Destinataire
                    </h2>
                    {client?.nom_affichage && <p><FaUser className="inline-block mr-2" /> {client.nom_affichage}</p>}
                    {client?.email && <p><FaInfoCircle className="inline-block mr-2" /> {client.email}</p>}
                    {client?.telephone && <p><FaCalendarAlt className="inline-block mr-2" /> {client.telephone}</p>}
                    {client?.siret && <p><FaStickyNote className="inline-block mr-2" /> {client.siret}</p>}
                </div>
            </div>

            {/* Détails de l'avoir */}
            <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-2">
                <p><FaCalendarAlt className="inline-block mr-2" /> <strong>Date d'émission :</strong> {avoir.date_emission}</p>
                <p><FaInfoCircle className="inline-block mr-2" /> <strong>Statut :</strong> {avoir.statut}</p>
                {avoir.notes_client && <p><FaStickyNote className="inline-block mr-2" /> <strong>Notes client :</strong> {avoir.notes_client}</p>}
                {avoir.notes_internes && <p><FaStickyNote className="inline-block mr-2" /> <strong>Notes internes :</strong> {avoir.notes_internes}</p>}
            </div>


            {/* Lignes */}
            <div className="bg-white shadow-md rounded-lg p-4">
                <h2 className="font-semibold mb-2">Lignes</h2>
                <table className="w-full text-sm border-collapse text-center">
                    <thead className="bg-color1 text-color2 uppercase text-xs font-semibold">
                        <tr>
                            <th className="px-3 py-2 text-left">Désignation</th>
                            <th className="px-3 py-2 w-24">Qté</th>
                            <th className="px-3 py-2 w-24">Unité</th>
                            <th className="px-3 py-2 w-32">Prix Unitaire</th>
                            {avoir.tva_centimes > 0 && <th className="px-3 py-2 w-24">TVA (%)</th>}
                            <th className="px-3 py-2 w-32">Total HT</th>
                        </tr>
                    </thead>
                    <tbody>
                        {avoir.lignes.map((ligne) => (
                            <tr key={ligne.id} className="border-b hover:bg-gray-50">
                                <td className="px-2 py-1 text-left">{ligne.designation}</td>
                                <td className="px-2 py-1">{ligne.quantite}</td>
                                <td className="px-2 py-1">{ligne.unite || "-"}</td>
                                <td className="px-2 py-1">{formatMoney(ligne.prix_unitaire_centimes)}</td>
                                {avoir.tva_centimes > 0 && <td className="px-2 py-1">{ligne.taux_tva || "-"}</td>}
                                <td className="px-2 py-1">{formatMoney(ligne.total_ligne_centimes)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Totaux détaillés */}
                <div className="grid grid-cols-[140px_120px] gap-y-1 justify-end items-end mt-4">
                    <span className="font-bold">Total HT :</span>
                    <span className="text-right">{formatMoney(totaux.sous_total_centimes)}</span>

                    {Object.keys(tvaParTaux).map(rate => (
                        <React.Fragment key={rate}>
                            <span className="font-bold">TVA ({formatRate(rate)}%) :</span>
                            <span className="text-right">{formatMoney(tvaParTaux[rate])}</span>
                        </React.Fragment>
                    ))}

                    <span className="font-bold">Total TVA :</span>
                    <span className="text-right">{formatMoney(totaux.tva_centimes)}</span>

                    <span className="font-bold">Total TTC :</span>
                    <span className="text-right font-semibold">{formatMoney(totaux.total_centimes)}</span>

                    <span className="font-bold text-red-600">Net à payer :</span>
                    <span className="text-right text-red-600 font-semibold">{formatMoney(totaux.net_a_payer)}</span>
                </div>
            </div>

            {/* Modale PDF */}
            {selectedPdf && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-transparent w-11/12 h-full rounded-lg shadow-lg p-2 relative">
                        <button
                            onClick={() => setSelectedPdf(null)}
                            className="absolute top-2 right-2 text-white bg-red-600 px-2 py-1 rounded"
                        >
                            X
                        </button>

                        {pdfLoading && (
                            <div className="flex justify-center items-center h-full">
                                <LoadingIcons.SpinningCircles
                                    stroke="#fff"
                                    fill="#fff"
                                    strokeWidth={3}
                                    height={40}
                                    width={40}
                                />
                                <span className="ml-3 text-white font-medium">Chargement du PDF...</span>
                            </div>
                        )}

                        <iframe
                            src={selectedPdf}
                            title="Avoir PDF"
                            className={`w-full h-full rounded-lg ${pdfLoading ? "hidden" : ""}`}
                            onLoad={() => setPdfLoading(false)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConsulterAvoir;
